import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import FleetTitle from '@/libs/antd/components/Title';
import { useSearchPurchaseOrders } from '@/libs/fleet-api/hooks/purchase-order';
import { ArrowRightOutlined, FileSearchOutlined } from '@ant-design/icons';
import { Button, Input, Select, Space, Table } from 'antd';
import { FC, useEffect, useState } from 'react';

/**
 * Type for PO List Table Row
 */
export interface POListItem {
	key: number;
	mklNo: string;
	spfNo: string;
	spfId: number;
	napResolutionId: number;
	mppNo: string;
	namaCustomer: string;
	customerId: string;
	groupBmpp: string;
	mppId: number;
	allowDetail: boolean;
}

/**
 * Purchase Order List Page
 */
const FRMPOListPage: FC = () => {
	const { navigate } = useApp();

	const { openCustomerDetail } = useModals();

	// Search options
	const searchByOptions = [
		{ id: 'customer-name', value: 'Nama Customer' },
		{ id: 'bmpp-group', value: 'Group BMPP' },
	];

	const [searchBy, setSearchBy] = useState<string>(searchByOptions[0].id);
	const [searchValue, setSearchValue] = useState('');
	const [page, setPage] = useState(1);
	const pageSize = 10;
	const [triggeredSearch, setTriggeredSearch] = useState<{
		by: string;
		value: string;
	} | null>(null);

	// Reset page to 1 on new search
	useEffect(() => {
		if (triggeredSearch) setPage(1);
	}, [triggeredSearch]);

	const { data, isLoading, error } = useSearchPurchaseOrders({
		pagination: { pageSize, pageNumber: page },
		...(triggeredSearch && triggeredSearch.value
			? { search: { by: triggeredSearch.by, value: triggeredSearch.value } }
			: {}),
	});

	const tableData: POListItem[] = (data?.items || []).map((item, idx) => ({
		key: idx + 1 + (page - 1) * pageSize,
		mklNo: item.mklNo,
		spfNo: item.spfNo,
		spfId: item.spfId,
		mppNo: item.mppNo,
		napResolutionId: item.napResolutionId,
		namaCustomer: item.customerName,
		customerId: item.customerId,
		groupBmpp: item.bmppGroupName,
		mppId: item.mppId,
		allowDetail: item.allowDetail,
	}));

	const handleDetailPO = (mppId: number, spfNo: string, mklNo: string) => {
		try {
			navigate('FRMPODetailListPage', { mppId, spfNo, mklNo });
		} catch (error) {
			console.error('Navigation error:', error);
		}
	};

	const handleAction = (
		mppId: number,
		mppNo: string,
		spfNo: string,
		mklNo: string,
	) => {
		navigate('FRMPOActionListPage', { mppId, mppNo, spfNo, mklNo });
	};

	const handleSearch = () => {
		setTriggeredSearch({ by: searchBy, value: searchValue });
		setPage(1);
	};

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<FleetTitle>Purchase Order (PO)</FleetTitle>
			<div style={{ display: 'flex', gap: 8, width: '100%' }}>
				<Select
					style={{ minWidth: 160, width: '40%' }}
					value={searchBy}
					onChange={setSearchBy}
					options={searchByOptions.map((opt) => ({
						label: opt.value,
						value: opt.id,
					}))}
					placeholder="Cari berdasarkan"
				/>
				<Input
					placeholder="Masukkan kata kunci..."
					value={searchValue}
					onChange={(e) => setSearchValue(e.target.value)}
					onPressEnter={handleSearch}
					style={{ borderRadius: 8, width: '40%' }}
					allowClear
				/>
				<Button
					type="primary"
					onClick={handleSearch}
					style={{
						width: 190,
					}}
				>
					SEARCH
				</Button>
			</div>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={tableData}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						render: (_, __, index) => (page - 1) * pageSize + index + 1,
					},
					{
						title: 'No.MKL',
						dataIndex: 'mklNo',
					},
					{
						title: 'No.SPF',
						dataIndex: 'spfNo',
						render: (v, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									// TODO: change to actual prospect id
									navigate('SPFPage', {
										spfId: record.spfId,
										napResolutionId: record.napResolutionId,
										viewOnly: true,
										onBack: () =>
											navigate('FRMPOListPage', {
												mppId: record.mppId,
												spfNo: record.spfNo,
												mklNo: record.mklNo,
											}),
									});
								}}
							>
								{v}
							</a>
						),
					},
					{
						title: 'No.MPP',
						dataIndex: 'mppNo',
						render: (v, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									navigate('MPPPage', {
										mppId: record.mppId,
										onBack: () =>
											navigate('FRMPOListPage', {
												mppId: record.mppId,
												spfNo: record.spfNo,
												mklNo: record.mklNo,
											}),
										viewOnly: true,
									});
								}}
							>
								{v}
							</a>
						),
					},
					{
						title: 'Nama Customer',
						dataIndex: 'namaCustomer',
						render: (v, record) =>
							record?.customerId ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(record.customerId);
									}}
								>
									{v}
								</a>
							) : (
								v
							),
					},
					{
						title: 'Group BMPP',
						dataIndex: 'groupBmpp',
					},
					{
						title: 'Detail PO',
						align: 'center',
						render: (_, record) => (
							<a
								onClick={
									record.allowDetail
										? (e) => {
												e.preventDefault();
												handleDetailPO(
													record.mppId,
													record.spfNo,
													record.mklNo,
												);
											}
										: undefined
								}
								style={
									record.allowDetail
										? { color: '#1557BC', cursor: 'pointer' }
										: { color: '#BCBCBC', cursor: 'default' }
								}
								aria-label="Detail PO"
							>
								<FileSearchOutlined />
							</a>
						),
					},
					{
						title: 'Action',
						align: 'center',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									handleAction(
										record.mppId,
										record.mppNo,
										record.spfNo,
										record.mklNo,
									);
								}}
								style={{ color: '#1557BC' }}
								aria-label="Action"
							>
								<ArrowRightOutlined />
							</a>
						),
					},
				]}
				pagination={{
					current: page,
					total: data?.totalCount || 0,
					pageSize,
					onChange: setPage,
				}}
				rowKey="key"
			/>
			{error && (
				<div style={{ color: 'red' }}>Gagal memuat data purchase order.</div>
			)}
		</Space>
	);
};

export default FRMPOListPage;
